/* eslint-disable no-mixed-operators */
/*
 * @FilePath: \kitchen-offline-web\src\3DEngine\render\renderTypes\renderLType.js
 * @Author: Yongchao Wang
 * @Date: 2021-08-01 14:04:57
 * @LastEditors: Xiaohua Zhu
 * @LastEditTime: 2022-08-18 10:03:46
 */
import { Vector3, Vector2 } from '@babylonjs/core/Maths/math';
import MeshManage from '@/3DEngine/render/meshManage';
import {
  FRAMECATEGORY, GAP_BETWEEN_WALL_AND_CABINET,
  MINIMUM_LTYPE_L1, MINIMUM_WALL_WIDTH, MINIMU_CAMERA_DISTANCE, SKIRTINGBOARDCONFIG, TOPFRAME_GROUND,
} from '@/3DEngine/render/utils/kitchenConfig';
import {
  checkFrameIsHighFrame, checkPreFrameIsHighFrame, putBottomCornerFrame,
} from '@/3DEngine/render/utils/renderUtils';
import RenderBaseType from '@/3DEngine/render/renderTypes/renderBaseType';
import {
  SidePlatePosition, SidePlateSequence, BASICTYPE_ENUM, LengthLocation, LengthType,
  CALLOUTLINE_TITLE, CALLOUTLINE_TYPE,
} from '@/3DEngine/render/utils/enum';
import LengthX from '../../lengthTypes/lengthX';
import LengthZ from '../../lengthTypes/lengthZ';
import Ground from '../../room/ground';
import LengthP2Z from '../../lengthTypes/lengthP2Z';
import LengthP2X from '../../lengthTypes/lengthP2X';
import HightFrame from '../../specialHandler/highFrame';
import {
  renderL1TopPanelUtil,
  renderL2TopPanelUtil,
  renderSkirtingBoardUtil,
} from './renderLTypeUtils';
import { getTopCabinetLengthInfo, calloutLineFilter } from '../../utils/lengthUtils';
import renderLP2XTop from '../../topCabinetSideChange/renderLTop';
import LengthZTop from '../../lengthTypes/lengthZTop';

class RenderLType extends RenderBaseType {
  /**
     * @description: 渲染L型顶柜
     * @param {*}
     * @return {*}
     * @author: Yongchao Wang
     */
  renderTop() {
    // L型的返回是一个数组 ['length1','length2'],['length2','length3']
    const { length } = getTopCabinetLengthInfo(this.templateJson?.top);
    if (length.length === 0) return;
    // 如果顶柜包括l3那么说明是切换之后的顶柜  应使用切换之后的顶柜摆放方式。
    if (length.includes('length3')) {
      this.isLTopChange = true;
      renderLP2XTop(this);
    } else {
      this.topLength1 = new LengthX(this.templateJson, this.scene, 'top', 'length1', this.copyTemplateJson);
      this.topLength2 = new LengthZ(this.templateJson, this.scene, 'top', 'length2', this.copyTemplateJson);
      this.putTopCupboard();
    }

    // 渲染补板
    // this.handleSealPlate(BASICTYPE_ENUM.top);
    if (this?.templateJson?.version > '2.2') {
      this.renderSidePlatesByType(SidePlatePosition.Top, length);
    } else {
      this.renderSidePlates(SidePlatePosition.Top);
    }
  }

  /**
     * @description: 渲染L型底柜
     * @param {*}
     * @return {*}
     * @author: Yongchao Wang
     */
  renderBottom() {
    if (!this?.templateJson?.bottom?.length1?.frames) {
      return;
    }
    this.bottomLength1 = new LengthX(this.templateJson, this.scene, 'bottom', 'length1', this.copyTemplateJson);
    this.bottomLength2 = new LengthZ(this.templateJson, this.scene, 'bottom', 'length2', this.copyTemplateJson);

    this.putBottomCupboard();
    // version2.0 remove by zhanyu.shen 20211228, 单独渲染踢脚板，顶柜有高柜影响踢脚板 see renderEngine/index
    // this.renderSkirtingBoard();
    this.renderTopPanel();
    // 渲染补板
    // this.handleSealPlate(BASICTYPE_ENUM.bottom);
    // version2.2+ 新的侧板渲染方法
    if (this?.templateJson?.version > '2.2') {
      this.renderSidePlatesByType(SidePlatePosition.Bottom);
    } else {
      this.renderSidePlates(SidePlatePosition.Bottom);
    }
  }

  renderSkirtingBoard() {
    renderSkirtingBoardUtil(this);
  }

  renderWindow() {
    const lengthArr = ['length1', 'length2'];
    let frame;
    let length;
    let window;
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < lengthArr.length; index++) {
      const element = lengthArr[index];
      // debugger;
      if (!frame) {
        const windowFrame = this.getWindowFrame('bottom', element);
        frame = windowFrame.frame;
        length = windowFrame.length;
        // 可能转角柜在L1,当时长边在L2
        if (frame?.longEdge) {
          length = `length${frame.longEdge}`;
        }
        window = windowFrame.window;
      }
    }
    switch (length) {
      case 'length1':
        this.bottomLength1.createWindow(frame, window);
        this.addMesh(this.bottomLength1?.windowNode?.windowMesh);
        break;
      case 'length2':
        this.bottomLength2.createWindow(frame, window);
        this.addMesh(this.bottomLength2?.windowNode?.windowMesh);
        break;
      default:
        break;
    }

    // 如果窗户隐藏自动隐藏窗户
    if (!this.windowVisiable) {
      this.showWindow(this.windowVisiable);
    }
  }

  renderRoom() {
    // 没有墙地面信息不渲染
    if (!this.templateJson?.other) return;
    const l2Width = this.bottomLength2.getSideLength(MINIMUM_WALL_WIDTH);
    const l1Width = this.bottomLength1.getSideLength(MINIMUM_LTYPE_L1);
    this.bottomLength1.createWall(l1Width);
    this.bottomLength2.createWall(l2Width);
    this.bottomLength3 = new LengthP2X(this.templateJson, this.scene, 'bottom', 'length3', this.copyTemplateJson);
    this.bottomLength3.createWall(l1Width, l2Width);
    this.bottomLength4 = new LengthP2Z(this.templateJson, this.scene, 'bottom', 'length4', this.copyTemplateJson);
    this.bottomLength4.createWall(l2Width, l1Width);
    const wallInfo = this.templateJson?.other?.wall;
    this.changeWallColor(wallInfo?.baseModelUrl,
      wallInfo?.normalModelUrl, { width: wallInfo?.width * 0.001, height: wallInfo?.height * 0.001 });
    this.groundNode = new Ground(this.scene, this.engine, { width: l1Width, height: l2Width });
    const grounds = [
      new Vector2(0, -GAP_BETWEEN_WALL_AND_CABINET),
      new Vector2(-l1Width, -GAP_BETWEEN_WALL_AND_CABINET),
      new Vector2(-l1Width, l2Width + 2 * GAP_BETWEEN_WALL_AND_CABINET),
      new Vector2(0, l2Width + 2 * GAP_BETWEEN_WALL_AND_CABINET),
    ];
    this.groundNode.createGround(grounds, new Vector3(0, 0, 0));
    this.groundNode.changeMaterial(this.templateJson?.other?.floor?.baseModelUrl,
      this.templateJson?.other?.floor?.normalModelUrl,
      { width: this.templateJson.other.floor.width * 0.001, height: this.templateJson.other.floor.height * 0.001 });
    this.roomMeshes = [
      this?.groundNode?.groundMesh,
      this.bottomLength1?.wallNode?.wallMesh,
      this.bottomLength2?.wallNode?.wallMesh,
      this.bottomLength3?.wallNode?.wallMesh,
      this.bottomLength4?.wallNode?.wallMesh,
    ];
    this.renderWindow();
  }


  /**
     * @description: 渲染底部/顶部侧板
     * @param {*}
     * @return {*}
     * @author: Xiaohua Zhu
     */
  renderSidePlates(position) {
    const lTypeLength1 = this.templateJson[position].length1;
    const lTypeLength2 = this.templateJson[position].length2;

    const l1SidePlatesArray = lTypeLength1.sidePlates;
    const l2SidePlatesArray = lTypeLength2.sidePlates;
    // L1边上的侧板
    if (l1SidePlatesArray?.length > 0) {
      const l1 = new LengthX(this.templateJson, this.scene);
      const l1LastFrameJson = lTypeLength1.frames[lTypeLength1.frames.length - 1];
      const l1LastSidePlateRotationData = l1.renderSidePlate(l1LastFrameJson, l1SidePlatesArray,
        lTypeLength1.frames.length === 1 ? SidePlateSequence.Corner : SidePlateSequence.Last, position);
      this.addSidePlatesMeshes(l1SidePlatesArray);
      this.addRotationMeshes(l1LastSidePlateRotationData);
    }
    // L2边上的侧板
    if (l2SidePlatesArray?.length > 0) {
      const l2 = new LengthZ(this.templateJson, this.scene);
      let l2LastFrameJson = lTypeLength2.frames[lTypeLength2.frames.length - 1];
      if (!l2LastFrameJson) {
        [l2LastFrameJson] = lTypeLength1.frames;
        const l2LastSidePlateRotationData = l2.renderSidePlate(l2LastFrameJson, l2SidePlatesArray,
          SidePlateSequence.Corner, position);
        this.addSidePlatesMeshes(l2SidePlatesArray);
        this.addRotationMeshes(l2LastSidePlateRotationData);
      } else {
        const l2LastSidePlateRotationData = l2.renderSidePlate(l2LastFrameJson, l2SidePlatesArray,
          SidePlateSequence.Last, position);
        this.addSidePlatesMeshes(l2SidePlatesArray);
        this.addRotationMeshes(l2LastSidePlateRotationData);
      }
    }
  }

  renderSidePlatesByType(position, length) {
    // 切换顶柜的时候可能是l2l3  所以不能写死顶柜的边取值  length确定是从l1 l2 l3的排列方式 所以直接取0和1
    let lTypeLength1;
    let lTypeLength2;
    // 只有顶柜且顶柜的length数组存在的时候采用length里面的数据
    if (position === 'top' && length) {
      lTypeLength1 = this.templateJson[position][length[0]];
      lTypeLength2 = this.templateJson[position][length[1]];
    } else {
      lTypeLength1 = this.templateJson[position].length1;
      lTypeLength2 = this.templateJson[position].length2;
    }
    const length1SidePlatesArr = lTypeLength1.sidePlatesV2;
    const length2SidePlatesArr = lTypeLength2.sidePlatesV2;
    this[`${position}Length1`].renderSidePlateByLength(length1SidePlatesArr, position);
    this[`${position}Length2`].renderSidePlateByLength(length2SidePlatesArr, position);
    if (this.refrigeraror?.type === position) {
      // 冰箱类返回的是冰箱所在的length  l型顶柜也是3  但是l的toplength只有1和2   所以当冰箱为3的时候将其修改为2保持一致
      this[`${position}Length${this.refrigeraror.length === 3 ? 2 : this.refrigeraror.length}`]
        .setRefrigerator(this.refrigeraror, position);
    }
  }


  setCalloutLine() {
    if (this.templateJson.bottom.type === 'L') {
      this.setBottomCalloutLine();
    }
    // version2.0 无顶柜的时候top为null
    if (this.templateJson.top?.type === 'L') {
      this.setTopCalloutLine();
    }
  }


  setBottomCalloutLine() {
    const { Length, EndHeight, EndDepth } = CALLOUTLINE_TYPE;
    const lTypeL1ShowList = [Length, EndHeight, EndDepth];
    const lTypeL2ShowList = [Length];
    const lTypeL2HighAndLowShowList = [Length, EndHeight, EndDepth];
    const isHighAndLow = this.templateJson.bottom.highAndLow;
    const calloutLineLength1 = this.bottomLength1?.getFramesCalloutLine(LengthLocation.Bottom);
    const calloutLineLength2 = this.bottomLength2?.getFramesCalloutLine(LengthLocation.Bottom);
    const calloutLines = {
      length1: calloutLineFilter(lTypeL1ShowList, calloutLineLength1),
      length2: calloutLineFilter(isHighAndLow ? lTypeL2HighAndLowShowList : lTypeL2ShowList, calloutLineLength2),
    };
    this.calloutLineInfo.bottom = calloutLines;
  }

  setTopCalloutLine() {
    const { Length, EndHeight, EndDepth } = CALLOUTLINE_TYPE;
    const lTypeL1ShowList = [Length, EndHeight, EndDepth];
    const lTypeL2ShowList = [Length, EndHeight, EndDepth];
    const otherSideShowList = [Length];

    if (this.isLTopChange) {
      const calloutLineLength1 = new LengthZTop(
        this.templateJson, this.scene, LengthLocation.Top, LengthType.Length2,
      ).getFramesCalloutLine(LengthLocation.Top, CALLOUTLINE_TITLE.L2);
      const calloutLineLength2 = this.topLength2?.getFramesCalloutLine(LengthLocation.Top, CALLOUTLINE_TITLE.L1);
      const calloutLines = {
        length2: calloutLineFilter(otherSideShowList, calloutLineLength1),
        length3: calloutLineFilter(lTypeL2ShowList, calloutLineLength2),
      };
      this.calloutLineInfo.top = calloutLines;
    } else {
      const calloutLineLength1 = this.topLength1?.getFramesCalloutLine(LengthLocation.Top);
      const calloutLineLength2 = this.topLength2?.getFramesCalloutLine(LengthLocation.Top);
      const calloutLines = {
        length1: calloutLineFilter(lTypeL1ShowList, calloutLineLength1),
        length2: calloutLineFilter(otherSideShowList, calloutLineLength2),
      };
      this.calloutLineInfo.top = calloutLines;
    }
  }

  /**
     * @description: 渲染台面
     * @param {*}
     * @return {*}
     * @author: Yongchao Wang
     */
  renderTopPanel() {
    const topPanelTexture = this?.templateJson?.bottom?.topPanels[0].componentBasic?.modelUrl;

    renderL1TopPanelUtil(topPanelTexture, this);
    renderL2TopPanelUtil(topPanelTexture, this);
  }


  /**
     * @description: 摆放底柜
     * @param {*}
     * @return {*}
     * @author: Yongchao Wang
     */
  putBottomCupboard() {
    // 底柜补板数据
    const sealPlateData = this.getLengthSealPlate(BASICTYPE_ENUM.bottom);
    const lengthTwo = this?.templateJson?.bottom?.length2?.frames?.length;
    let datumPoint = null;
    let sealPlateMesh = null;
    const sealPlateLengthXPos = this.bottomLength1.getSealPlatePos(this.templateJson, BASICTYPE_ENUM.bottom);
    const sealPlateLengthZPos = this.bottomLength2.getSealPlatePos(this.templateJson, BASICTYPE_ENUM.bottom);

    // 移动柜子到指定位置
    this?.templateJson?.bottom?.length1?.frames.forEach((element, index) => {
      // 第一个柜子放在默认位置，第二个开始往右边排列，这个和后台align好的
      if (index > 0) {
        const preEl = this.templateJson.bottom.length1.frames[index - 1];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        // 摆放补板后柜体处理
        if (sealPlateMesh && sealPlateLengthXPos && index === sealPlateLengthXPos) {
          datumPoint = sealPlateMesh.getDatumPoint({ x: -1, y: 1, z: 0 });
        } else {
          datumPoint = preMeshManage.getDatumPoint({ x: -1, y: 1, z: 0 });
        }
        const { rotation } = meshManage.moveModelToPosition(datumPoint, { x: -1, y: 1, z: 0 });
        this.rotationMeshes[element.componentBasic.id] = rotation;
      } else {
        const { rotation } = putBottomCornerFrame(element, undefined, this.scene);
        this.rotationMeshes[element.componentBasic.id] = rotation;
      }
      if (element.ventilator) {
        let tmpRotation = { x: 0, y: 0, z: 0 };
        // 当前为l1的模型数组 如果灶台在转角柜上面且长边在2 那么旋转 -Math.PI/2
        if (element.longEdge === 2 && element.categoryId === FRAMECATEGORY.CATEGORY_28) {
          tmpRotation = { x: 0, y: -Math.PI / 2, z: 0 };
        }
        this.renderVentilator(element, tmpRotation);
      }
      // 补板处理 需要在摆完柜子之后进行
      if (sealPlateLengthXPos && index === sealPlateLengthXPos - 1) {
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        // 渲染补板
        sealPlateMesh = this.bottomLength1.renderSealPlate(
          sealPlateData.length1, meshManage, BASICTYPE_ENUM.bottom, element, this,
        );
      }
    });

    datumPoint = null; // 还原默认值
    sealPlateMesh = null; // 还原默认值
    // 特殊处理lengthZ 没有柜子和只有一个柜子
    if (!lengthTwo || !sealPlateLengthZPos) {
      // 摆放补板模型
      const preEl = this?.templateJson?.bottom?.length1?.frames[0];
      const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
      if (sealPlateData.length2.length) {
        sealPlateMesh = this.bottomLength2.renderSealPlate(
          sealPlateData.length2, preMeshManage, BASICTYPE_ENUM.bottom, preEl, this,
        );
      }
    }
    this?.templateJson?.bottom?.length2?.frames.forEach((element, index) => {
      // 第一个柜子放在默认位置，第二个开始往外边排列，这个和后台align好的
      if (index > 0) {
        const preEl = this?.templateJson?.bottom?.length2?.frames[index - 1];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));

        // 摆放补板后柜体处理
        if (sealPlateLengthZPos && index === sealPlateLengthZPos) {
          datumPoint = sealPlateMesh.getDatumPoint({ x: 0, y: 1, z: 1 });
        } else {
          datumPoint = preMeshManage.getDatumPoint({ x: 0, y: 1, z: 1 });
        }
        const { rotation } = meshManage.moveModelToPosition(datumPoint, { x: 0, y: 1, z: 1 });
        // 顶部高柜需要移动 x
        if (sealPlateLengthZPos && index === sealPlateLengthZPos) {
          meshManage.mesh.position.x = -meshManage.getModelSize().x * 0.5;
        }
        this.rotationMeshes[element.componentBasic.id] = rotation;
      } else {
        const preEl = this?.templateJson?.bottom?.length1?.frames[0];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        const rotation = meshManage.addRotation(0, -Math.PI / 2, 0);
        this.rotationMeshes[element.componentBasic.id] = rotation;

        const moveSealPlateZ = sealPlateMesh ? sealPlateMesh.getModelSize().z : 0;

        // 该地方取得加载之后的模型的size不是旋转之后的 所以直接拼接即可 不需要处理
        meshManage.setModelPosition({
          x: -meshManage.getModelSize().z * 0.5,
          y: SKIRTINGBOARDCONFIG.HEIGHT,
          z: meshManage.getModelSize().x * 0.5 + preMeshManage.getModelSize().z + moveSealPlateZ,
        });
      }
      if (element.ventilator) {
        let tmpRotation = { x: 0, y: -Math.PI / 2, z: 0 };
        // 当前为l2边的模型数组 如果在转角柜上面且长边在3那么需要旋转 -Math.PI
        if (element.longEdge === 3 && element.categoryId === FRAMECATEGORY.CATEGORY_28) {
          tmpRotation = { x: 0, y: -Math.PI, z: 0 };
        }
        this.renderVentilator(element, tmpRotation);
      }
      // 补板处理 需要在摆完柜子之后进行
      if (sealPlateLengthZPos && index === sealPlateLengthZPos - 1) {
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        // 渲染补板
        sealPlateMesh = this.bottomLength2.renderSealPlate(
          sealPlateData.length2, meshManage, BASICTYPE_ENUM.bottom, element, this,
        );
      }
    });
  }


  /**
     * @description: 摆放顶柜
     * @param {*}
     * @return {*}
     * @author: Yongchao Wang
     */
  putTopCupboard() {
    const sealPlateData = this.getLengthSealPlate(BASICTYPE_ENUM.top);
    const topToGround = TOPFRAME_GROUND(this.templateJson);
    // const otherSealPlateLength2 = this?.templateJson?.top?.length2?.otherSealPlateLength;
    // const hasLength2HightFrame = checkouHasHightFrame(this?.templateJson, 'length2');
    const frameLengthZ = this?.templateJson?.top?.length2?.frames?.length;

    let datumPoint = null;
    let sealPlateMesh = null;
    const sealPlateLengthXPos = this.topLength1.getSealPlatePos(this.templateJson, BASICTYPE_ENUM.top);
    const sealPlateLengthZPos = this.topLength2.getSealPlatePos(this.templateJson, BASICTYPE_ENUM.top);
    // 移动柜子到指定位置
    this?.templateJson?.top?.length1?.frames.forEach((element, index) => {
      // 一期空高柜判断
      if (HightFrame.checkEmptyPlaceholderHighFrame(element)) {
        return;
      }
      if (checkPreFrameIsHighFrame(this?.templateJson?.top?.length1?.frames, index)) {
        const preEl = this?.templateJson?.top?.length1?.frames[index - 1];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        const point = preMeshManage.getDatumPoint({ x: -1, y: 1, z: 0 });
        point.y = topToGround - meshManage.getModelSize().y;
        const { rotation } = meshManage.moveModelToPosition(point, { x: -1, y: 1, z: 0 });
        this.rotationMeshes[element.componentBasic.id] = rotation;
        // 第一个柜子放在默认位置，第二个开始往右边排列，这个和后台align好的
      } else if (index > 0) {
        const preEl = this?.templateJson?.top?.length1?.frames[index - 1];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        // 摆放补板后柜体处理
        if (sealPlateMesh && sealPlateLengthXPos && index === sealPlateLengthXPos) {
          datumPoint = sealPlateMesh.getDatumPoint({ x: -1, y: 1, z: 0 });
        } else {
          datumPoint = preMeshManage.getDatumPoint({ x: -1, y: 1, z: 0 });
        }
        const { rotation } = meshManage.moveModelToPosition(datumPoint, { x: -1, y: 1, z: 0 });
        // 顶部高柜需要移动 y
        if (checkFrameIsHighFrame(element)) {
          meshManage.mesh.position.y = SKIRTINGBOARDCONFIG.HEIGHT;
        }
        this.rotationMeshes[element.componentBasic.id] = rotation;
      } else {
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        meshManage.setModelPosition({
          x: -meshManage.getModelSize().x * 0.5,
          y: topToGround - meshManage.getModelSize().y,
          z: meshManage.getModelSize().z * 0.5,
        });
      }
      // 补板处理 需要在摆完柜子之后进行
      if (sealPlateLengthXPos && index === sealPlateLengthXPos - 1) {
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        // 渲染补板
        sealPlateMesh = this.topLength1.renderSealPlate(
          sealPlateData.length1, meshManage, BASICTYPE_ENUM.top, element, this,
        );
      }
    });

    datumPoint = null; // 还原默认值
    sealPlateMesh = null; // 还原默认值
    // 特殊处理lengthZ 没有柜子和只有一个柜子
    if (!frameLengthZ || frameLengthZ === 1 || !sealPlateLengthZPos) {
      // 摆放补板模型
      const preEl = this?.templateJson?.top?.length1?.frames[0];
      const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
      if (sealPlateData.length2.length) {
        sealPlateMesh = this.topLength2.renderSealPlate(
          sealPlateData.length2, preMeshManage, BASICTYPE_ENUM.top, preEl, this,
        );
      }
    }
    // 移动柜子到指定位置
    this?.templateJson?.top?.length2?.frames.forEach((element, index) => {
      // if (checkFrameIsHighFrame(element)) {
      //   return;
      // }
      if (checkPreFrameIsHighFrame(this?.templateJson?.top?.length2?.frames, index)) {
        const preEl = this?.templateJson?.top?.length2?.frames[index - 1];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        const point = preMeshManage.getDatumPoint({ x: 0, y: 1, z: 1 });
        point.y = topToGround - meshManage.getModelSize().y;
        const { rotation } = meshManage.moveModelToPosition(point, { x: 0, y: 1, z: 1 });
        this.rotationMeshes[element.componentBasic.id] = rotation;

        // 第一个柜子放在默认位置，第二个开始往外边排列，这个和后台align好的
      } else if (index > 0) {
        const preEl = this?.templateJson?.top?.length2?.frames[index - 1];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));
        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));

        // 摆放补板后柜体处理
        if (sealPlateLengthZPos && index === sealPlateLengthZPos) {
          sealPlateMesh = this.topLength2.renderSealPlate(
            sealPlateData.length2, preMeshManage, BASICTYPE_ENUM.top, preEl,
            this,
          );
          datumPoint = sealPlateMesh.getDatumPoint({ x: 0, y: 1, z: 1 });
        } else {
          datumPoint = preMeshManage.getDatumPoint({ x: 0, y: 1, z: 1 });
        }
        const { rotation } = meshManage.moveModelToPosition(datumPoint, { x: 0, y: 1, z: 1 });
        // 顶部高柜需要移动 x
        if (checkFrameIsHighFrame(element)) {
          if (index === sealPlateLengthZPos) {
            meshManage.mesh.position.x -= -preMeshManage.getModelSize().x - sealPlateMesh.getModelSize().x;
          }
          meshManage.mesh.position.y = SKIRTINGBOARDCONFIG.HEIGHT;
        }
        this.rotationMeshes[element.componentBasic.id] = rotation;
      } else {
        const preEl = this?.templateJson?.top?.length1?.frames[0];
        const preMeshManage = new MeshManage(this.scene.getMeshByName(preEl.componentBasic.id));

        const meshManage = new MeshManage(this.scene.getMeshByName(element.componentBasic.id));
        const rotation = meshManage.addRotation(0, -Math.PI / 2, 0);
        this.rotationMeshes[element.componentBasic.id] = rotation;

        const moveZ = sealPlateMesh ? sealPlateMesh.getModelSize().z : 0;
        meshManage.setModelPosition({
          x: -meshManage.getModelSize().z * 0.5 - 0.005,
          y: topToGround - meshManage.getModelSize().y,
          z: meshManage.getModelSize().x * 0.5 + preMeshManage.getModelSize().z + moveZ,
        });
      }
    });
  }


  /**
     * @description: 获取相机最佳位置的参数
     * @param {*}
     * @return {*} {target:Vector3,position:Vector3}
     * @author: Yongchao Wang
     */
  getCameraBestConfig(fov, allMeshes, camera) {
    const max = new Vector3(0, TOPFRAME_GROUND(this.templateJson),
      this.bottomLength2.getSideLength(MINIMU_CAMERA_DISTANCE));
    const min = new Vector3(-this.bottomLength1.getSideLength(MINIMUM_LTYPE_L1), 0, 0);
    const size = { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z };
    // 视线目标是场景中心点，高度与中心点一致
    const position = {};

    // eslint-disable-next-line no-underscore-dangle
    const ratio = this.scene._engine.getAspectRatio(camera);
    // 计算横向视角刚好看到所有柜子，视角中线垂直与L两边连线，并与相机形成等腰三角形
    const hFov = (fov * ratio) / 2;
    const L1Length = Math.max(this.templateJson.bottom.length1.length, this.templateJson.top?.length1?.length ?? 0);
    const L2Length = Math.max(this.templateJson.bottom.length2.length, this.templateJson.top?.length2?.length ?? 0);

    const L1Longer = L1Length > L2Length;
    console.log(L1Longer);
    const point1 = { x: -size.x, z: 0 };
    const point2 = { x: 0, z: size.z };
    const halfPoint = { x: point1.x / 2 + point2.x / 2, z: point2.z / 2 + point1.z / 2 };// 斜线中心点
    const distance = Math.sqrt((point1.x - point2.x) ** 2 + (point1.z - point2.z) ** 2) / 2;// 斜线距离的一半

    let hDistance = distance / Math.tan(hFov); // 对边长度

    const vDistance = max.y / 2 / Math.tan(fov / 2);

    hDistance = Math.max(hDistance, vDistance) + 0.9;

    // 斜率 求target与postion 连线y=kx+a
    const k = -((point1.x - point2.x) / (point1.z - point2.z));
    const ka = halfPoint.z - k * halfPoint.x;
    // position点的直线方程y=kx+a，又(y-y1)²+(x-x1)²=hDistance²
    // x1,y1已知就是halfPoint,联合解一元二次方程
    // x =[-b+√(b^2-4ac)]/2a
    // x= [-b-√(b^2-4ac)]/2a
    const a = k ** 2 + 1;
    const b = -2 * halfPoint.x + 2 * (ka - halfPoint.z) * k;
    const c = halfPoint.x ** 2 + (ka - halfPoint.z) ** 2 - hDistance ** 2;
    // 一元二次方程求解
    const x1 = (-b + Math.sqrt(b ** 2 - 4 * a * c)) / (2 * a);
    const x2 = (-b - Math.sqrt(b ** 2 - 4 * a * c)) / (2 * a);
    const x = Math.min(x1, x2);
    const z = k * x + ka;

    position.z = z;
    position.x = x;
    position.y = size.y / 2 + 0.5;
    const target = {};
    target.y = size.y / 2;
    target.x = halfPoint.x;
    target.z = halfPoint.z;
    return {
      target: new Vector3(
        target.x,
        target.y,
        target.z,
      ),
      position: new Vector3(
        position.x,
        position.y,
        position.z,
      ),
      lowerAlphaLimit: Math.PI / 2,
      upperAlphaLimit: Math.PI,
      alpha: 2.265125041777525,
      beta: 1.4492930597593832,

    };
  }
}


export default RenderLType;
